import { useState, useEffect } from "react";
import api from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../hooks/use-toast";

// ─── Link form (shown when not connected) ────────────────────────────────────
function LinkForm({ userEmail, onLinked }) {
  const [email, setEmail] = useState(userEmail || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/odysboard/link", {
        odysboard_email: email !== userEmail ? email : null,
        odysboard_password: password,
      });
      toast({ title: "Odysboard connecté !" });
      onLinked();
    } catch (err) {
      toast({ variant: "destructive", title: "Erreur", description: err.response?.data?.detail || "Connexion impossible" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-8 max-w-md w-full animate-fade-in-up">
        <div className="text-center mb-6">
          <div className="icon-circle icon-circle--ink mx-auto mb-4" style={{ width: "64px", height: "64px" }}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: "28px", height: "28px" }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>Connecter Odysboard</h3>
          <p className="text-sm text-gray-500 mt-2">Synchronisez vos données d'investissement</p>
        </div>

        <form onSubmit={handleLink} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-2">Email Odysboard</label>
            <input
              data-testid="odysboard-email-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all"
              placeholder="votre@email.com"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-2">Mot de passe</label>
            <input
              data-testid="odysboard-password-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all"
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="link-odysboard-btn"
            type="submit"
            disabled={loading || !password}
            className="btn-primary w-full py-3 text-sm disabled:opacity-50"
          >
            {loading ? "Connexion..." : "Connecter mon Odysboard"}
          </button>
        </form>

        <p className="text-xs text-center text-gray-400 mt-5">
          Connexion sécurisée · Token crypté
        </p>
      </div>
    </div>
  );
}

// ─── Metric Card ──────────────────────────────────────────────────────────────
const MetricCard = ({ icon, value, label, trend, color = "cyan" }) => {
  const colors = {
    cyan: "from-cyan-500/20 to-cyan-600/10 border-cyan-500/30 text-cyan-400",
    violet: "from-violet-500/20 to-violet-600/10 border-violet-500/30 text-violet-400",
    emerald: "from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-400",
    amber: "from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-400",
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-4 backdrop-blur-sm`}>
      <div className="flex items-start justify-between">
        <div className="text-2xl">{icon}</div>
        {trend && <span className={`text-xs font-medium ${trend > 0 ? "text-emerald-400" : "text-red-400"}`}>{trend > 0 ? "+" : ""}{trend}%</span>}
      </div>
      <p className="text-2xl font-bold text-gray-900 mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
};

// ─── Annonces Section ─────────────────────────────────────────────────────────
function AnnoncesSection({ items }) {
  if (!items?.length) return (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      </div>
      <p className="text-gray-500 text-sm">Aucune annonce enregistrée</p>
    </div>
  );

  const statusConfig = {
    won: { label: "Gagné", bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200" },
    visit: { label: "Visite", bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200" },
    offer: { label: "Offre", bg: "bg-violet-100", text: "text-violet-700", border: "border-violet-200" },
    analyse: { label: "Analyse", bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200" },
  };

  return (
    <div className="space-y-3">
      {items.slice(0, 10).map((a, i) => {
        const status = statusConfig[a.crmStatus] || statusConfig.analyse;
        const rendement = a.loyerEstime && a.prix ? ((a.loyerEstime * 12) / a.prix * 100).toFixed(1) : null;
        return (
          <div key={i} className="bg-gray-50 rounded-xl border border-gray-200 p-4 hover:border-violet-300 hover:bg-white transition-all group">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${status.bg} ${status.text} ${status.border} border`}>
                    {status.label}
                  </span>
                  {a.priorite && <span className="text-xs text-amber-600 font-medium">Priorité {a.priorite}</span>}
                </div>
                <h4 className="font-semibold text-gray-900 text-sm truncate group-hover:text-violet-600 transition-colors">{a.titre || "Sans titre"}</h4>
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                    {a.ville || "-"}
                  </span>
                  <span>{a.typeBien || "-"}</span>
                  {a.surface && <span>{a.surface} m²</span>}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {a.prix ? `${Number(a.prix).toLocaleString("fr-FR")} €` : "-"}
                </p>
                {a.loyerEstime && (
                  <p className="text-xs text-emerald-600 mt-1">
                    Loyer: {Number(a.loyerEstime).toLocaleString("fr-FR")} €/mois
                  </p>
                )}
                {rendement && (
                  <p className={`text-xs font-semibold mt-1 ${Number(rendement) >= 7 ? "text-emerald-600" : Number(rendement) >= 5 ? "text-amber-600" : "text-red-500"}`}>
                    {rendement}% brut
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Simulations Section (avec vue détaillée "Résultats Clés" style Odysboard) ────────────────
function SimulationsSection({ items }) {
  const [modalSim, setModalSim] = useState(null);

  if (!items?.length) return (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      </div>
      <p className="text-gray-500 text-sm">Aucune simulation financière</p>
    </div>
  );

  // Helper to format numbers
  const formatNum = (val, suffix = "") => {
    if (val === null || val === undefined || val === "") return null;
    const num = Number(val);
    if (isNaN(num)) return null;
    return `${num.toLocaleString("fr-FR")}${suffix}`;
  };

  const formatPercent = (val) => {
    if (val === null || val === undefined || val === "") return null;
    const num = Number(val);
    if (isNaN(num)) return null;
    return `${num.toFixed(2)}%`;
  };

  // Deep search for a value in simulation object - based on actual Odysboard structure
  // Data is in: donneesEntree (inputs) and resultats (calculated)
  const findValue = (s, ...keys) => {
    if (!s) return null;
    
    // Search in these objects in order of priority
    const searchObjects = [
      s.resultats || {},           // Calculated results
      s.donneesEntree || {},       // Input parameters
      s,                           // Root level
    ];
    
    for (const key of keys) {
      for (const obj of searchObjects) {
        const val = obj[key];
        if (val !== undefined && val !== null && val !== "" && val !== "-") {
          return val;
        }
      }
    }
    return null;
  };

  // Extract all result keys from simulation - EXACT mapping based on Odysboard JSON structure
  const getResultsData = (s) => {
    const res = s.resultats || {};
    const input = s.donneesEntree || {};
    
    // Calcul de la Rentabilité Patrimoniale (Loyers + Plus-value)
    // = Rendement Net + Plus-value annuelle prévue
    const rendementNet = res.rendementNet || 0;
    const plusValueAnnuelle = input.plusValueAnnuelle || 0;
    const rentaPatrimonialeCalc = rendementNet + plusValueAnnuelle;
    
    // Calcul du ROI (Return On Investment) avec effet de levier
    // = (Cash-flow annuel en croisière + Plus-value annuelle estimée) / Apport personnel × 100
    const cashFlowAnnuel = (res.cashFlowCroisiere || 0) * 12;
    const prixAchat = input.prixAchat || 0;
    const apport = input.apportPersonnel || 1; // Éviter division par zéro
    const plusValueEstimee = prixAchat * (plusValueAnnuelle / 100);
    const gainsAnnuels = cashFlowAnnuel + plusValueEstimee;
    const roiCalc = (gainsAnnuels / apport) * 100;
    
    return {
      // ═══ RENTABILITÉS (from resultats) ═══
      rentaBrute: res.rendementBrut,
      rentaNette: res.rendementNet,
      // Rentabilité Patrimoniale = Rendement Net + Plus-value annuelle
      rentaPatrimoniale: rentaPatrimonialeCalc > 0 ? rentaPatrimonialeCalc : null,
      // ROI avec effet de levier
      roi: roiCalc > 0 ? roiCalc : null,
      
      // ═══ CASHFLOWS (from resultats) ═══
      cashflowCroisiere: res.cashFlowCroisiere,           // En croisière (stable)
      cashflowDiffere: res.cashFlowMensuel,               // Pendant différé (temporary)
      cashflowMensuel: res.cashFlowMensuelAvantImpots,    // Before taxes
      
      // ═══ TRÉSORERIE (from resultats) ═══
      tresorerieAnnee1: res.tresorerieAnnee1,
      moisTravaux: input.dureeTravaux,
      moisDiffere: input.dureeDiffere,
      
      // ═══ INFOS BIEN (from donneesEntree) ═══
      prixAchat: input.prixAchat,
      loyerMensuel: input.loyerMensuel,
      surface: input.surface,
      typeBien: input.typeBien || input.typeLocation,
      ville: s.ville || input.ville,
      regimeFiscal: input.regimeFiscal,
      
      // ═══ FINANCEMENT ═══
      montantEmprunte: res.montantEmprunte || input.montantEmprunte,
      apport: input.apportPersonnel,
      tauxInteret: input.tauxInteret,
      dureeCredit: input.dureePret,
      mensualiteCredit: res.mensualiteTotal || res.mensualiteApresDiffere,
      coutTotal: res.prixTotal,
      
      // ═══ FRAIS (mixed) ═══
      fraisNotaire: res.fraisNotaire,             // Calculated amount in resultats
      fraisBancaires: input.fraisBancaires,
      travaux: input.travaux,
      fraisAgence: res.fraisAgence || input.fraisAgenceTransaction,
      
      // ═══ CHARGES (from donneesEntree) ═══
      chargesMensuelles: input.chargesMensuelles,
      taxeFonciere: input.taxeFonciere,
      assurancePNO: input.assurancePNO,
      gestionLocative: input.gestionLocative || input.fraisGestionAgence,
      vacanceLocative: input.vacanceLocative,
      
      // ═══ EXTRA DATA ═══
      chargesAnnuelles: res.chargesAnnuelles,
      loyersAnnuels: res.loyersAnnuelsApresVacance,
      plusValueBrute: res.plusValueBrute,
      plusValueNette: res.plusValueNette,
      prixRevente: res.prixRevente,
      coutTotalCredit: res.coutTotalCredit,
      plusValueAnnuelle: input.plusValueAnnuelle,
    };
  };

  return (
    <>
      {/* Modal pour les résultats détaillés - Style Odysboard Dark Theme */}
      {modalSim && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setModalSim(null)}>
          <div 
            className="bg-slate-900 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in-up border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-5 rounded-t-2xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {modalSim.nom || "Simulation"}
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">
                    {modalSim.dateCreation ? new Date(modalSim.dateCreation).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : ""}
                    {(modalSim.ville || findValue(modalSim, "ville")) && ` • ${modalSim.ville || findValue(modalSim, "ville")}`}
                  </p>
                </div>
                <button 
                  onClick={() => setModalSim(null)}
                  className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all border border-slate-600"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {(() => {
                const data = getResultsData(modalSim);
                
                return (
                  <>
                    {/* ═══════ SECTION: RÉSULTATS CLÉS (5 cartes comme sur l'image) ═══════ */}
                    <div>
                      <h4 className="text-lg font-bold text-cyan-400 mb-5 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Résultats Clés
                      </h4>
                      
                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                        {/* Card 1: Rentabilité Brute */}
                        <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700 text-center">
                          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                            <svg className="w-10 h-10 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                          </div>
                          <p className="text-3xl font-bold text-cyan-400 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {data.rentaBrute !== null ? formatPercent(data.rentaBrute) : "-"}
                          </p>
                          <p className="text-xs text-slate-400">Rentabilité brute</p>
                        </div>

                        {/* Card 2: Rentabilité Nette */}
                        <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700 text-center">
                          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                            <svg className="w-10 h-10 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                            </svg>
                          </div>
                          <p className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {data.rentaNette !== null ? formatPercent(data.rentaNette) : "-"}
                          </p>
                          <p className="text-xs text-slate-400">Rentabilité nette</p>
                        </div>

                        {/* Card 3: Cash-flow NET (avec 2 valeurs) */}
                        <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700 text-center">
                          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                            <svg className="w-10 h-10 text-violet-500" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17 2H7c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H7V4h10v16z"/>
                              <rect x="9" y="6" width="6" height="2" fill="currentColor"/>
                            </svg>
                          </div>
                          <p className="text-xs text-slate-400 mb-2">Cash-flow NET</p>
                          
                          {/* En croisière */}
                          <div className="bg-teal-900/30 border border-teal-500/30 rounded-lg p-2 mb-2">
                            <p className="text-[10px] text-teal-400 flex items-center justify-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
                              EN CROISIÈRE
                            </p>
                            <p className="text-[10px] text-teal-500">(représentatif)</p>
                            <p className={`text-xl font-bold ${(data.cashflowCroisiere !== null && Number(data.cashflowCroisiere) >= 0) ? "text-teal-400" : "text-red-400"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                              {data.cashflowCroisiere !== null ? `${Number(data.cashflowCroisiere) >= 0 ? "+" : ""}${formatNum(data.cashflowCroisiere, "€")}` : (data.cashflowMensuel !== null ? `${Number(data.cashflowMensuel) >= 0 ? "+" : ""}${formatNum(data.cashflowMensuel, "€")}` : "-")}
                            </p>
                          </div>
                          
                          {/* Pendant différé */}
                          <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-2">
                            <p className="text-[10px] text-slate-400 flex items-center justify-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-500"></span>
                              Pendant différé (temporaire)
                            </p>
                            <p className={`text-lg font-bold ${(data.cashflowDiffere !== null && Number(data.cashflowDiffere) >= 0) ? "text-teal-400" : "text-white"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                              {data.cashflowDiffere !== null ? `${Number(data.cashflowDiffere) >= 0 ? "+" : ""}${formatNum(data.cashflowDiffere, "€")}` : "-"}
                            </p>
                          </div>
                          <p className="text-[10px] text-slate-500 mt-2">(après impôts)</p>
                        </div>

                        {/* Card 4: Rentabilité Patrimoniale */}
                        <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700 text-center">
                          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                            <svg className="w-10 h-10 text-violet-400" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                            </svg>
                          </div>
                          <p className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {data.rentaPatrimoniale !== null ? formatPercent(data.rentaPatrimoniale) : "-"}
                          </p>
                          <p className="text-xs text-slate-400">Rentabilité Patrimoniale</p>
                          <p className="text-[10px] text-slate-500 mt-1">Loyers + Plus-value</p>
                        </div>

                        {/* Card 5: ROI (Return On Investment) */}
                        <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700 text-center">
                          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                            <svg className="w-10 h-10 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M7 17.5c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.6L5.2 14c-.1.3-.2.6-.2 1 0 1.1.9 2 2 2h12v-2H7.4c-.1 0-.2-.1-.2-.2v-.1l.9-1.7h7.4c.8 0 1.4-.4 1.7-1l3.6-6.5c.1-.2.2-.4.2-.6 0-.6-.4-1-1-1H5.2l-.9-2H1z"/>
                            </svg>
                          </div>
                          <p className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {data.roi !== null ? formatPercent(data.roi) : "-"}
                          </p>
                          <p className="text-xs text-slate-400">ROI (Return On Investment)</p>
                          <p className="text-[10px] text-slate-500 mt-1">Effet de levier</p>
                        </div>
                      </div>
                    </div>

                    {/* ═══════ SECTION: TRÉSORERIE À PRÉVOIR ═══════ */}
                    {(data.tresorerieAnnee1 !== null || data.moisTravaux !== null || data.moisDiffere !== null) && (
                      <div className="bg-gradient-to-r from-amber-600 to-orange-500 rounded-2xl p-5 border border-amber-400">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-amber-700/50 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-amber-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                              Trésorerie à prévoir année 1
                            </h4>
                            <p className="text-3xl font-bold text-white mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                              {data.tresorerieAnnee1 !== null ? formatNum(data.tresorerieAnnee1, " €") : "-"}
                            </p>
                            <p className="text-sm text-amber-100 mt-2">
                              Besoin de trésorerie pendant 12 mois de l'année 1.
                              {data.moisTravaux !== null && ` (${data.moisTravaux} mois de travaux`}
                              {data.moisDiffere !== null && ` + ${data.moisDiffere} mois de différé)`}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ═══════ SECTION: INFORMATIONS DÉTAILLÉES ═══════ */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Informations du Bien */}
                      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
                        <h4 className="text-sm font-bold text-violet-400 mb-4 flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          Informations du Bien
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-slate-500">Prix d'achat</p>
                            <p className="text-sm font-semibold text-white">{data.prixAchat !== null ? formatNum(data.prixAchat, " €") : "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Loyer mensuel</p>
                            <p className="text-sm font-semibold text-emerald-400">{data.loyerMensuel !== null ? formatNum(data.loyerMensuel, " €") : "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Surface</p>
                            <p className="text-sm font-semibold text-white">{data.surface !== null ? formatNum(data.surface, " m²") : "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Type de bien</p>
                            <p className="text-sm font-semibold text-white">{data.typeBien || "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Ville</p>
                            <p className="text-sm font-semibold text-white">{data.ville || "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Régime fiscal</p>
                            <p className="text-sm font-semibold text-violet-400">{data.regimeFiscal || "-"}</p>
                          </div>
                        </div>
                      </div>

                      {/* Financement */}
                      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
                        <h4 className="text-sm font-bold text-cyan-400 mb-4 flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Financement
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-slate-500">Montant emprunté</p>
                            <p className="text-sm font-semibold text-white">{data.montantEmprunte !== null ? formatNum(data.montantEmprunte, " €") : "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Apport personnel</p>
                            <p className="text-sm font-semibold text-cyan-400">{data.apport !== null ? formatNum(data.apport, " €") : "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Taux d'intérêt</p>
                            <p className="text-sm font-semibold text-white">{data.tauxInteret !== null ? formatPercent(data.tauxInteret) : "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Durée du crédit</p>
                            <p className="text-sm font-semibold text-white">{data.dureeCredit !== null ? `${data.dureeCredit} ans` : "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Mensualité crédit</p>
                            <p className="text-sm font-semibold text-red-400">{data.mensualiteCredit !== null ? formatNum(data.mensualiteCredit, " €") : "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Coût total projet</p>
                            <p className="text-sm font-semibold text-white">{data.coutTotal !== null ? formatNum(data.coutTotal, " €") : "-"}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Frais et Charges */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
                      <h4 className="text-sm font-bold text-amber-400 mb-4 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                        </svg>
                        Frais & Charges
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-slate-500">Frais de notaire</p>
                          <p className="text-sm font-semibold text-white">{data.fraisNotaire !== null ? formatNum(data.fraisNotaire, " €") : "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Travaux</p>
                          <p className="text-sm font-semibold text-amber-400">{data.travaux !== null ? formatNum(data.travaux, " €") : "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Taxe foncière / an</p>
                          <p className="text-sm font-semibold text-white">{data.taxeFonciere !== null ? formatNum(data.taxeFonciere, " €") : "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Charges mensuelles</p>
                          <p className="text-sm font-semibold text-white">{data.chargesMensuelles !== null ? formatNum(data.chargesMensuelles, " €") : "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Assurance PNO / an</p>
                          <p className="text-sm font-semibold text-white">{data.assurancePNO !== null ? formatNum(data.assurancePNO, " €") : "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Gestion locative</p>
                          <p className="text-sm font-semibold text-white">{data.gestionLocative !== null ? formatPercent(data.gestionLocative) : "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Vacance locative</p>
                          <p className="text-sm font-semibold text-white">{data.vacanceLocative !== null ? formatPercent(data.vacanceLocative) : "-"}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Frais bancaires</p>
                          <p className="text-sm font-semibold text-white">{data.fraisBancaires !== null ? formatNum(data.fraisBancaires, " €") : "-"}</p>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Liste des simulations */}
      <div className="space-y-3">
        {items.slice(0, 12).map((s, i) => {
          // Accès direct aux données selon la structure Odysboard
          const res = s.resultats || {};
          const input = s.donneesEntree || {};
          
          const rentaBrute = res.rendementBrut;
          const rentaNette = res.rendementNet;
          const cashflow = res.cashFlowCroisiere;
          const prixAchat = input.prixAchat;
          const ville = s.ville || input.ville;

          return (
            <div 
              key={i} 
              data-testid={`simulation-item-${i}`}
              className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:border-violet-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() => setModalSim(s)}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900 text-sm">{s.nom || `Simulation ${i + 1}`}</h4>
                      <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-medium">
                        Voir détails
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {s.dateCreation ? new Date(s.dateCreation).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : ""}
                      {ville && ` • ${ville}`}
                      {prixAchat && ` • ${Number(prixAchat).toLocaleString("fr-FR")} €`}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3 justify-end">
                    {rentaBrute !== null && rentaBrute !== undefined && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Renta. brute</p>
                        <p className={`text-lg font-bold ${Number(rentaBrute) >= 7 ? "text-emerald-600" : Number(rentaBrute) >= 5 ? "text-amber-600" : "text-red-500"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                          {Number(rentaBrute).toFixed(2)}%
                        </p>
                      </div>
                    )}
                    {rentaNette !== null && rentaNette !== undefined && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Renta. nette</p>
                        <p className={`text-lg font-bold ${Number(rentaNette) >= 5 ? "text-emerald-600" : Number(rentaNette) >= 3 ? "text-amber-600" : "text-red-500"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                          {Number(rentaNette).toFixed(2)}%
                        </p>
                      </div>
                    )}
                    {cashflow !== null && cashflow !== undefined && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Cash-flow</p>
                        <p className={`text-lg font-bold ${Number(cashflow) >= 0 ? "text-emerald-600" : "text-red-500"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                          {Number(cashflow) >= 0 ? "+" : ""}{Number(cashflow).toFixed(0)} €
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─── Visites Section ──────────────────────────────────────────────────────────
function VisitesSection({ items }) {
  if (!items?.length) return (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
      <p className="text-gray-500 text-sm">Aucune visite planifiée</p>
    </div>
  );

  const statusConfig = {
    planifiee: { label: "Planifiée", bg: "bg-blue-500/20", text: "text-blue-400" },
    programmee: { label: "Programmée", bg: "bg-blue-500/20", text: "text-blue-400" },
    confirmee: { label: "Confirmée", bg: "bg-violet-500/20", text: "text-violet-400" },
    realisee: { label: "Réalisée", bg: "bg-emerald-500/20", text: "text-emerald-400" },
    annulee: { label: "Annulée", bg: "bg-red-500/20", text: "text-red-400" },
    reportee: { label: "Reportée", bg: "bg-amber-500/20", text: "text-amber-400" },
  };

  return (
    <div className="space-y-3">
      {items.slice(0, 10).map((v, i) => {
        const status = statusConfig[v.statut] || statusConfig.planifiee;
        const date = v.dateVisite ? new Date(v.dateVisite) : null;
        return (
          <div key={i} className="bg-gray-100/50 backdrop-blur-sm rounded-xl border border-gray-200/50 p-4 hover:border-emerald-500/30 transition-all">
            <div className="flex items-center gap-4">
              {date && (
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-cyan-500/30 flex flex-col items-center justify-center flex-shrink-0">
                  <span className="text-lg font-bold text-gray-900 leading-none">{date.getDate()}</span>
                  <span className="text-[10px] text-cyan-400 uppercase font-medium">{date.toLocaleString("fr-FR", { month: "short" })}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${status.bg} ${status.text}`}>
                    {status.label}
                  </span>
                  {v.heureVisite && <span className="text-xs text-gray-500">{v.heureVisite}</span>}
                </div>
                <p className="font-medium text-gray-900 text-sm truncate">{v.bienVisiter || "Bien sans nom"}</p>
                {v.agentContact && <p className="text-xs text-gray-500 mt-1">Contact: {v.agentContact}</p>}
                {v.questionsPoints && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{v.questionsPoints}</p>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Villes Section (Vue améliorée avec top villes + tableau) ───────────────────────────────────────────
function VillesSection({ items }) {
  if (!items?.length) return (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <p className="text-gray-500 text-sm">Aucune ville analysée</p>
    </div>
  );

  // Helper to get value from ville object - based on actual Odysboard structure
  const getVal = (v, key) => v?.[key];

  // Calculate top cities with EXACT field names from Odysboard
  const topInvest = items.reduce((best, v) => {
    const score = Number(v.scoreGlobal || 0);
    const bestScore = Number(best?.scoreGlobal || 0);
    return score > bestScore ? v : best;
  }, items[0]);

  const topRendement = items.reduce((best, v) => {
    const rend = Number(v.rendement || 0);  // "rendement" not "rendementLocatif"
    const bestRend = Number(best?.rendement || 0);
    return rend > bestRend ? v : best;
  }, items[0]);

  const topDynamique = items.reduce((best, v) => {
    const dyn = Number(v.tensionLocative || v.ratioLocataires || 0);  // "tensionLocative" or "ratioLocataires"
    const bestDyn = Number(best?.tensionLocative || best?.ratioLocataires || 0);
    return dyn > bestDyn ? v : best;
  }, items[0]);

  const topPlusValue = items.reduce((best, v) => {
    const pv = Number(v.evolutionPrix1an || 0);  // "evolutionPrix1an" not "evolutionPrix"
    const bestPv = Number(best?.evolutionPrix1an || 0);
    return pv > bestPv ? v : best;
  }, items[0]);

  return (
    <div className="space-y-6">
      {/* Top Cities Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Top 1 pour investir */}
        <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-5 border border-slate-700 text-center">
          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
            <svg className="w-10 h-10 text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </div>
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Top 1 pour investir</p>
          <p className="text-2xl font-bold text-cyan-400 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{topInvest?.nom || "-"}</p>
          <p className="text-lg font-semibold text-white">{topInvest?.scoreGlobal || "-"}/100</p>
          <p className="text-xs text-slate-500 mt-1">{topInvest?.region || ""}</p>
        </div>

        {/* Meilleur rendement */}
        <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-5 border border-slate-700 text-center">
          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
            <svg className="w-10 h-10 text-emerald-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C13.1 2 14 2.9 14 4V8H16V4C16 1.79 14.21 0 12 0S8 1.79 8 4V8H10V4C10 2.9 10.9 2 12 2ZM19 9H5C3.9 9 3 9.9 3 11V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V11C21 9.9 20.1 9 19 9ZM12 17C10.9 17 10 16.1 10 15S10.9 13 12 13 14 13.9 14 15 13.1 17 12 17Z"/>
            </svg>
          </div>
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Meilleur rendement</p>
          <p className="text-2xl font-bold text-emerald-400 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{topRendement?.nom || "-"}</p>
          <p className="text-lg font-semibold text-white">{topRendement?.rendement ? `${Number(topRendement.rendement).toFixed(1)}%` : "-"}</p>
          <p className="text-xs text-slate-500 mt-1">{topRendement?.region || ""}</p>
        </div>

        {/* Ville la + dynamique */}
        <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-5 border border-slate-700 text-center">
          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
            <svg className="w-10 h-10 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z"/>
            </svg>
          </div>
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Ville la + dynamique</p>
          <p className="text-2xl font-bold text-amber-400 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{topDynamique?.nom || "-"}</p>
          <p className="text-lg font-semibold text-white">{topDynamique?.ratioLocataires ? `${topDynamique.ratioLocataires}%` : (topDynamique?.tensionLocative || "-")}</p>
          <p className="text-xs text-slate-500 mt-1">{topDynamique?.region || ""}</p>
        </div>

        {/* Meilleur potentiel plus-value */}
        <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-5 border border-slate-700 text-center">
          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
            <svg className="w-10 h-10 text-violet-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 13h1v7c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-7h1a1 1 0 00.7-1.7l-9-9a.996.996 0 00-1.4 0l-9 9A1 1 0 003 13z"/>
            </svg>
          </div>
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Meilleur potentiel plus-value</p>
          <p className="text-2xl font-bold text-violet-400 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{topPlusValue?.nom || "-"}</p>
          <p className="text-lg font-semibold text-white">{topPlusValue?.evolutionPrix1an ? `+${Number(topPlusValue.evolutionPrix1an).toFixed(1)}%/an` : "-"}</p>
          <p className="text-xs text-slate-500 mt-1">{topPlusValue?.region || ""}</p>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-slate-900 rounded-2xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-800/50">
                <th className="text-left py-4 px-4 font-medium text-cyan-400">Ville</th>
                <th className="text-center py-4 px-3 font-medium text-cyan-400">Population</th>
                <th className="text-center py-4 px-3 font-medium text-cyan-400">Prix au m²</th>
                <th className="text-center py-4 px-3 font-medium text-cyan-400">Rendement</th>
                <th className="text-center py-4 px-3 font-medium text-cyan-400">Évolution prix</th>
                <th className="text-center py-4 px-3 font-medium text-cyan-400">Ratio locataires</th>
                <th className="text-center py-4 px-3 font-medium text-cyan-400">Services</th>
                <th className="text-center py-4 px-3 font-medium text-cyan-400">Transport</th>
                <th className="text-center py-4 px-3 font-medium text-cyan-400">Score global</th>
              </tr>
            </thead>
            <tbody>
              {items.map((v, i) => {
                // Accès direct aux champs exacts de Odysboard
                const score = Number(v.scoreGlobal || 0);
                const rend = Number(v.rendement || 0);
                const evol = Number(v.evolutionPrix1an || 0);
                const tension = v.tensionLocative || v.ratioLocataires;
                
                return (
                  <tr key={i} className="border-b border-slate-800 hover:bg-slate-800/50 transition-colors">
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-semibold text-white">{v.nom || "Ville"}</p>
                        <p className="text-xs text-slate-500">{v.codePostal || ""}{v.codePostal && v.region ? " - " : ""}{v.region || ""}</p>
                      </div>
                    </td>
                    <td className="py-4 px-3 text-center text-slate-300">
                      {v.population ? Number(v.population).toLocaleString("fr-FR") : "-"}
                    </td>
                    <td className="py-4 px-3 text-center">
                      <div>
                        <p className="text-white font-medium">{v.prixM2 ? `${Number(v.prixM2).toLocaleString("fr-FR")}€/m²` : "-"}</p>
                        {v.loyerM2 > 0 && <p className="text-xs text-slate-500">{Number(v.loyerM2).toFixed(1)}€/m²/mois</p>}
                      </div>
                    </td>
                    <td className="py-4 px-3 text-center">
                      <span className={`font-bold ${rend >= 7 ? "text-emerald-400" : rend >= 5 ? "text-amber-400" : rend > 0 ? "text-red-400" : "text-slate-500"}`}>
                        {rend > 0 ? `${rend.toFixed(1)}%` : "-"}
                      </span>
                    </td>
                    <td className="py-4 px-3 text-center">
                      <span className={`font-medium ${evol > 0 ? "text-emerald-400" : evol < 0 ? "text-red-400" : "text-slate-400"}`}>
                        {evol !== 0 ? `${evol > 0 ? "+" : ""}${evol.toFixed(1)}%` : "N/A"}
                      </span>
                    </td>
                    <td className="py-4 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className={`w-2 h-2 rounded-full ${Number(tension || 0) >= 50 ? "bg-emerald-400" : Number(tension || 0) > 0 ? "bg-amber-400" : "bg-slate-600"}`}></span>
                        <span className="text-slate-300">{tension || "-"}{tension ? "%" : ""}</span>
                      </div>
                    </td>
                    <td className="py-4 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className={`w-2 h-2 rounded-full ${Number(v.noteServices || 0) >= 7 ? "bg-emerald-400" : Number(v.noteServices || 0) > 0 ? "bg-amber-400" : "bg-slate-600"}`}></span>
                        <span className="text-slate-300">{v.noteServices || "-"}/10</span>
                      </div>
                    </td>
                    <td className="py-4 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className={`w-2 h-2 rounded-full ${Number(v.noteTransport || 0) >= 7 ? "bg-emerald-400" : Number(v.noteTransport || 0) > 0 ? "bg-amber-400" : "bg-slate-600"}`}></span>
                        <span className="text-slate-300">{v.noteTransport || "-"}/10</span>
                      </div>
                    </td>
                    <td className="py-4 px-3 text-center">
                      <span className={`inline-flex items-center justify-center px-3 py-1.5 rounded-lg font-bold ${
                        score >= 70 ? "bg-emerald-500/20 text-emerald-400" : 
                        score >= 50 ? "bg-amber-500/20 text-amber-400" : score > 0 ? "bg-red-500/20 text-red-400" : "bg-slate-700 text-slate-400"
                      }`}>
                        {score > 0 ? `${score}/100` : "-"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Coach Comments Section ───────────────────────────────────────────────────
function CoachCommentsSection({ studentId, isCoachView }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [category, setCategory] = useState("general");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const categories = [
    { key: "general", label: "Général", icon: "💬" },
    { key: "simulation", label: "Simulation", icon: "📊" },
    { key: "annonce", label: "Annonce", icon: "🏠" },
    { key: "visite", label: "Visite", icon: "📅" },
    { key: "conseil", label: "Conseil", icon: "💡" },
  ];

  useEffect(() => {
    fetchComments();
  }, [studentId]);

  const fetchComments = async () => {
    try {
      const res = await api.get(`/coach/comments/${studentId}`);
      setComments(res.data);
    } catch (err) {
      console.error("Failed to fetch comments", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setLoading(true);
    try {
      await api.post(`/coach/comments/${studentId}`, { content: newComment, category });
      setNewComment("");
      toast({ title: "Commentaire ajouté" });
      fetchComments();
    } catch (err) {
      toast({ variant: "destructive", title: "Erreur", description: err.response?.data?.detail || "Erreur" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce commentaire ?")) return;
    try {
      await api.delete(`/coach/comments/${id}`);
      toast({ title: "Commentaire supprimé" });
      fetchComments();
    } catch (err) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const getCategoryIcon = (cat) => categories.find(c => c.key === cat)?.icon || "💬";

  return (
    <div className="space-y-4">
      {/* Add comment form (coach only) */}
      {isCoachView && (
        <form onSubmit={handleSubmit} className="bg-gray-100/50 backdrop-blur-sm rounded-xl border border-gray-200/50 p-4">
          <div className="flex items-center gap-2 mb-3">
            <p className="text-sm font-medium text-gray-900">Ajouter une observation</p>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="ml-auto px-3 py-1.5 rounded-lg bg-slate-700/50 border border-gray-300/50 text-sm text-gray-900 focus:outline-none focus:border-cyan-500"
            >
              {categories.map(c => (
                <option key={c.key} value={c.key}>{c.icon} {c.label}</option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="coach-comment-input"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Écrivez votre observation ou conseil..."
            rows={3}
            className="w-full px-4 py-3 rounded-xl bg-gray-50/50 border border-gray-300/50 text-gray-900 text-sm placeholder-slate-500 focus:outline-none focus:border-cyan-500 resize-none"
          />
          <div className="flex justify-end mt-3">
            <button
              data-testid="submit-coach-comment-btn"
              type="submit"
              disabled={loading || !newComment.trim()}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {loading ? "Envoi..." : "Publier"}
            </button>
          </div>
        </form>
      )}

      {/* Comments list */}
      {comments.length === 0 ? (
        <div className="text-center py-8 bg-gray-100/30 rounded-xl border border-gray-200/30">
          <p className="text-gray-500 text-sm">
            {isCoachView ? "Aucune observation pour le moment. Ajoutez votre premier commentaire !" : "Votre coach n'a pas encore ajouté d'observations."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="bg-gray-100/50 backdrop-blur-sm rounded-xl border border-gray-200/50 p-4 hover:border-cyan-500/20 transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{getCategoryIcon(c.category)}</span>
                    <span className="text-xs font-medium text-cyan-400">{c.coach_name}</span>
                    <span className="text-xs text-slate-600">•</span>
                    <span className="text-xs text-gray-500">
                      {new Date(c.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{c.content}</p>
                </div>
                {isCoachView && (
                  <button
                    onClick={() => handleDelete(c.id)}
                    className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Odysboard View ──────────────────────────────────────────────────────
export default function OdysboardView({ studentId, isCoachView = false }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, linked: false, data: null, email: null, studentName: null });
  const [tab, setTab] = useState("overview");
  const { toast } = useToast();

  const fetchData = async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const endpoint = isCoachView && studentId
        ? `/odysboard/student/${studentId}`
        : "/odysboard/data";
      const res = await api.get(endpoint);
      setState({ 
        loading: false, 
        linked: res.data.linked, 
        data: res.data.data, 
        email: res.data.email,
        studentName: res.data.student_name || null
      });
    } catch (err) {
      if (err.response?.status === 404) {
        setState({ loading: false, linked: false, data: null, email: null, studentName: null });
      } else {
        toast({ variant: "destructive", title: "Erreur Odysboard", description: err.response?.data?.detail || "Erreur de chargement" });
        setState(s => ({ ...s, loading: false }));
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setState(s => ({ ...s, loading: true }));
      try {
        const endpoint = isCoachView && studentId
          ? `/odysboard/student/${studentId}`
          : "/odysboard/data";
        const res = await api.get(endpoint);
        if (!cancelled) setState({ 
          loading: false, 
          linked: res.data.linked, 
          data: res.data.data, 
          email: res.data.email,
          studentName: res.data.student_name || null
        });
      } catch (err) {
        // Ne pas traiter 401 comme une erreur fatale - juste considérer comme non-lié
        if (!cancelled) {
          console.log("Odysboard fetch error:", err.response?.status);
          setState({ loading: false, linked: false, data: null, email: null, studentName: null });
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [studentId, isCoachView]);

  const handleUnlink = async () => {
    if (!window.confirm("Délier votre compte Odysboard ?")) return;
    try {
      await api.delete("/odysboard/unlink");
      setState({ loading: false, linked: false, data: null, email: null, studentName: null });
      toast({ title: "Compte Odysboard délié" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  // Loading state
  if (state.loading) return (
    <div className="p-6 space-y-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-24 rounded-2xl bg-gray-100/50 animate-pulse" />
      ))}
    </div>
  );

  // Coach view: student hasn't linked yet
  if (isCoachView && !state.linked) return (
    <div className="p-6">
      <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/30 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-lg font-semibold text-amber-400" style={{ fontFamily: "Outfit, sans-serif" }}>Odysboard non connecté</p>
        <p className="text-sm text-gray-500 mt-2">Cet élève n'a pas encore lié son compte Odysboard à la plateforme.</p>
        
        {/* Coach can still add comments */}
        <div className="mt-8 pt-6 border-t border-gray-200/50">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Vos observations</h4>
          <CoachCommentsSection studentId={studentId} isCoachView={true} />
        </div>
      </div>
    </div>
  );

  // Student: not linked yet
  if (!isCoachView && !state.linked) return (
    <div className="p-6">
      <LinkForm userEmail={user?.email} onLinked={fetchData} />
    </div>
  );

  const d = state.data || {};
  const tabs = [
    { key: "overview", label: "Vue d'ensemble", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
    { key: "annonces", label: `Annonces (${d.annonces?.length || 0})`, icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
    { key: "simulations", label: `Simulations (${d.simulations?.length || 0})`, icon: "M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" },
    { key: "visites", label: `Visites (${d.visites?.length || 0})`, icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
    { key: "villes", label: `Villes (${d.villes?.length || 0})`, icon: "M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" },
    ...(isCoachView ? [{ key: "comments", label: "Observations", icon: "M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" }] : []),
  ];

  // Calculate summary stats
  const totalAnnonces = d.annonces?.length || 0;
  const totalSimulations = d.simulations?.length || 0;
  const totalVisites = d.visites?.length || 0;
  const totalVilles = d.villes?.length || 0;
  const avgRendement = d.simulations?.length 
    ? (d.simulations.reduce((sum, s) => sum + (s.resultats?.rendementBrut || s.resultats?.rendBrut || 0), 0) / d.simulations.length).toFixed(1)
    : null;

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* Header - Dossier Bancaire ink card */}
      <div className="rounded-2xl p-6 shadow-lg" style={{ background: "linear-gradient(135deg, var(--ink) 0%, var(--ink-dark) 100%)", color: "#FFFFFF", borderTop: "3px solid var(--gold)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "rgba(181, 137, 74, 0.18)", border: "1px solid rgba(233, 220, 196, 0.35)" }}>
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: "var(--gold-soft)" }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "var(--serif)" }}>
                {isCoachView && state.studentName ? `Odysboard de ${state.studentName}` : "Mon Odysboard"}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
                <span className="text-sm text-white/80">{state.email} · Synchronisé</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* SSO Button - Open Odysboard */}
            {!isCoachView && (
              <button 
                onClick={async () => {
                  try {
                    const res = await api.get("/odysboard/sso-token");
                    // Open Odysboard with token
                    const url = new URL("https://odys-board.odysinvest.com/dashboard.html");
                    url.searchParams.set("sso_token", res.data.token);
                    url.searchParams.set("uid", res.data.uid);
                    window.open(url.toString(), "_blank");
                  } catch (e) {
                    // Fallback: open login page
                    window.open("https://odys-board.odysinvest.com/login-styled.html", "_blank");
                  }
                }}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2 bg-white text-violet-600 shadow-lg"
                title="Ouvrir Odysboard"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Ouvrir Odysboard
              </button>
            )}
            <button 
              onClick={fetchData} 
              className="p-2.5 rounded-xl bg-white/20 text-gray-900 hover:bg-white/30 transition-all"
              title="Actualiser"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            {!isCoachView && (
              <button 
                onClick={handleUnlink} 
                className="px-3 py-2 rounded-xl bg-white/20 text-gray-900 text-sm font-medium hover:bg-red-500/50 transition-all"
              >
                Délier
              </button>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
            <div className="text-2xl font-bold">{totalAnnonces}</div>
            <div className="text-sm text-gray-900/70">Annonces</div>
          </div>
          <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
            <div className="text-2xl font-bold">{totalSimulations}</div>
            <div className="text-sm text-gray-900/70">Simulations</div>
          </div>
          <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
            <div className="text-2xl font-bold">{totalVisites}</div>
            <div className="text-sm text-gray-900/70">Visites</div>
          </div>
          <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
            <div className="text-2xl font-bold">{totalVilles}</div>
            <div className="text-sm text-gray-900/70">Villes</div>
          </div>
          {avgRendement && (
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
              <div className="text-2xl font-bold">{avgRendement}%</div>
              <div className="text-sm text-gray-900/70">Rend. moyen</div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl border border-gray-200 overflow-x-auto">
        {tabs.map(t => (
          <button 
            key={t.key} 
            data-testid={`ody-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              tab === t.key 
                ? "bg-white text-gray-900 shadow-sm border border-gray-200" 
                : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
            }`}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.icon} />
            </svg>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
        {tab === "overview" && (
          <div className="space-y-6">
            {/* Weekly metrics */}
            <div className="bg-gradient-to-r from-blue-50 to-violet-50 rounded-xl border border-blue-100 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Activité de la semaine
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-2xl font-bold text-gray-900">
                    {d.annonces?.filter(a => {
                      const date = new Date(a.dateAjout || a.createdAt);
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return date > weekAgo;
                    }).length || 0}
                  </p>
                  <p className="text-xs text-gray-500">Annonces ajoutées</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-2xl font-bold text-gray-900">
                    {d.simulations?.filter(s => {
                      const date = new Date(s.dateCreation || s.createdAt);
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return date > weekAgo;
                    }).length || 0}
                  </p>
                  <p className="text-xs text-gray-500">Simulations créées</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-2xl font-bold text-gray-900">
                    {d.visites?.filter(v => {
                      const date = new Date(v.dateVisite);
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return date > weekAgo;
                    }).length || 0}
                  </p>
                  <p className="text-xs text-gray-500">Visites cette semaine</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-2xl font-bold text-gray-900">
                    {d.villes?.filter(v => {
                      const date = new Date(v.dateAjout || v.createdAt);
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return date > weekAgo;
                    }).length || 0}
                  </p>
                  <p className="text-xs text-gray-500">Villes analysées</p>
                </div>
              </div>
            </div>

            {/* Best simulation highlight */}
            {d.simulations?.length > 0 && (() => {
              const bestSim = d.simulations.reduce((best, s) => {
                const res = s.resultats || {};
                const rendBrut = Number(res.rendementBrut || res.rendBrut || 0);
                const bestRendBrut = Number(best?.resultats?.rendementBrut || best?.resultats?.rendBrut || 0);
                return rendBrut > bestRendBrut ? s : best;
              }, d.simulations[0]);
              const res = bestSim?.resultats || {};
              const rendBrut = res.rendementBrut || res.rendBrut;
              const rendNet = res.rendementNet || res.rendNet;
              const cashflow = res.cashflowMensuel || res.cashFlow;
              
              if (!rendBrut) return null;
              
              return (
                <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 rounded-xl border border-emerald-200 p-5">
                  <h3 className="text-sm font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    Meilleure simulation
                  </h3>
                  <div className="flex flex-wrap items-center gap-6">
                    <div>
                      <p className="text-lg font-bold text-gray-900">{bestSim.nom || "Simulation"}</p>
                      <p className="text-xs text-gray-500">{bestSim.prixAchat ? `${Number(bestSim.prixAchat).toLocaleString("fr-FR")} €` : ""}</p>
                    </div>
                    <div className="flex gap-6">
                      {rendBrut && (
                        <div className="text-center">
                          <p className="text-2xl font-bold text-emerald-600">{Number(rendBrut).toFixed(1)}%</p>
                          <p className="text-xs text-gray-500">Rend. brut</p>
                        </div>
                      )}
                      {rendNet && (
                        <div className="text-center">
                          <p className="text-2xl font-bold text-emerald-600">{Number(rendNet).toFixed(1)}%</p>
                          <p className="text-xs text-gray-500">Rend. net</p>
                        </div>
                      )}
                      {cashflow && (
                        <div className="text-center">
                          <p className={`text-2xl font-bold ${Number(cashflow) >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                            {Number(cashflow) >= 0 ? "+" : ""}{Number(cashflow).toFixed(0)} €
                          </p>
                          <p className="text-xs text-gray-500">Cashflow/mois</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Latest activity summary */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Dernières annonces
                </h3>
                <AnnoncesSection items={d.annonces?.slice(0, 3)} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Dernières simulations
                </h3>
                <SimulationsSection items={d.simulations?.slice(0, 3)} />
              </div>
            </div>

            {/* Cahier des charges */}
            {d.cahierDesCharges && typeof d.cahierDesCharges === "string" && d.cahierDesCharges.trim() && (
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-violet-700 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Cahier des charges Odysboard
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{d.cahierDesCharges}</p>
              </div>
            )}

            {/* Coach comments (if coach view) */}
            {isCoachView && studentId && (
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  Vos observations
                </h3>
                <CoachCommentsSection studentId={studentId} isCoachView={true} />
              </div>
            )}
          </div>
        )}

        {tab === "annonces" && <AnnoncesSection items={d.annonces} />}
        {tab === "simulations" && <SimulationsSection items={d.simulations} />}
        {tab === "visites" && <VisitesSection items={d.visites} />}
        {tab === "villes" && <VillesSection items={d.villes} />}
        {tab === "comments" && isCoachView && studentId && (
          <CoachCommentsSection studentId={studentId} isCoachView={true} />
        )}
      </div>
    </div>
  );
}
